import { CloudTeams } from 'js/common/utils/bugsnag';

interface ClusterBuilderArgs {
  clusterName?: string;
  query?: string;
}

export const PageNames = {
  CLUSTER_BUILDER: 'Cluster Builder',
  PATH_SELECTOR: 'Cluster Builder Path Selector',
  CLUSTER_DETAIL: 'Cluster Detail',
  CLUSTER_CARD: 'Cluster Card',
  CLUSTER_CONNECT_MODAL: 'Connection Modal',
  CLUSTER_ATLAS_SEARCH: 'Atlas Search',
  PERSONALIZATION_WIZARD: 'Personalization Wizard',
  QUICK_ACCESS_SETUP: 'Quick Access Setup',
  ORGANIZATIONS_LINK_MARKETPLACE: 'Organizations - Link Marketplace',
  CREATE_ORGANIZATION: 'Create Organization',
  ONLINE_ARCHIVE: 'Online Archive',
  PAYMENT: 'Payment',
  PROJECT_SETTINGS: 'Project Settings',
  ORGANIZATION_SETTINGS: 'Organization Settings',
  BILLING: 'Billing',
} as const;

export enum FromLocation {
  UPGRADE_CTA = 'ctaUpgrade',
  EMAIL_CTA = 'ctaEmail',
  CLUSTER_HEADER_CTA = 'ctaClusterHeader',
  // Don't actually expect any "from" params to come through as 'DEFAULT'--this is mapped to the fallback value in FromLocationTrackingContexts
  DEFAULT = 'DEFAULT',
  // Not used for analytics tracking, need this to be able to go back to the right page from support FPP page
  SUPPORT = 'support',
  SUPPORT_MATRIX = 'supportMatrix',
  MILESTONES_SIDEBAR = 'milestonesSidebar',
}

export const FromLocationTrackingContexts: { [Path in FromLocation]: string } = {
  [FromLocation.UPGRADE_CTA]: 'Cluster Builder',
  [FromLocation.EMAIL_CTA]: 'Cluster Builder from Manage Project Alerts',
  [FromLocation.CLUSTER_HEADER_CTA]: 'Cluster Builder',
  [FromLocation.DEFAULT]: 'Cluster Builder',
  [FromLocation.SUPPORT]: 'Support',
  [FromLocation.SUPPORT_MATRIX]: 'Support Matrix',
  [FromLocation.MILESTONES_SIDEBAR]: 'Milestones Sidebar',
};

export const Routes = {
  PersonalizationWizard: () => 'setup/personalization',
  QuickAccessSetup: ({ includeToast = false }: { includeToast?: boolean } = {}) =>
    `setup/access${includeToast ? '?includeToast=true' : ''}`,
  ClusterCard: ({ fastPoll = false }: { fastPoll?: boolean } = {}) => `clusters${fastPoll ? '?fastPoll=true' : ''}`,
  ClusterDetail: () => 'clusters/detail',
  ClusterBuilder: (args?: ClusterBuilderArgs) => {
    if (args) {
      const { clusterName, query } = args;
      return `clusters/edit${clusterName ? `/${clusterName}` : ''}${query ? `?${query}` : ''}`;
    }
    return 'clusters/edit';
  },
  ClusterAtlasSearch: () => 'clusters/atlasSearch',
  ClusterConnectModal: () => 'clusters/connect',
  PathSelector: () => 'clusters/pathSelector',
  Charts: () => 'charts',
  NTMCInterstitial: () => 'inviteNewUsers',
  DatabaseAccess: () => 'security/database',
  NetworkAccess: () => 'security/network',
  ProjectSettings: () => 'settings/groupSettings',
  ClusterOnlineArchive: () => 'clusters/onlineArchive',
  UniversalAddData: () => 'addData',
  UniversalAddDataBuildCluster: () => 'addData/buildCluster',
  UniversalAddDataCreate: (activeClusterName) => `addData/${activeClusterName}/create`,
  UniversalAddDataCreateOnAtlas: (activeClusterName) => `addData/${activeClusterName}/create/atlas`,
  UniversalAddDataCreateOnApplication: (activeClusterName) => `addData/${activeClusterName}/create/application`,
  UniversalAddDataLoad: (activeClusterName) => `addData/${activeClusterName}/load`,
  UniversalAddDataMigrate: (activeClusterName) => `addData/${activeClusterName}/migrate`,
  UniversalAddDataImport: (activeClusterName) => `addData/${activeClusterName}/import`,
};

/**
 * Naming standard: {EXPERIMENT_NAME}_EXPERIMENT_YYYYMMDD, where
 * the date is the prod branch cut date.
 * EXAMPLE: FULL_PAGE_PAYMENT_CLICK_20210519
 */
export enum TestName {
  fakeTestForUnitTesting = 'FAKE_TEST_FOR_UNIT_TESTING',
  cmabToggle = 'CMAB_TOGGLE_20220317',
  pathSelectorTitlesCmab = 'PATH_SELECTOR_TITLES_CMAB_20220317',
  fullPagePaymentClick = 'FULL_PAGE_PAYMENT_CLICK_20210519',
  emailVerification = 'EMAIL_VERIFICATION_20210309',
  flexConsultingUI = 'FLEX_CONSULTING_UI_20210707',
  pholiotaAaTest = 'PHOLIOTA_AA_TEST_07212021',
  pholiotaAaTest2 = 'PHOLIOTA_AA_TEST_07212021_2',
  pholiotaAaTest3 = 'PHOLIOTA_AA_TEST_07212021_3',
  newTeamMemberCredits = 'NEW_TEAM_MEMBER_CREDITS_20210914',
  clusterCardPreviewImage = 'CLUSTER_CARD_PREVIEW_IMAGE_20211020',
  integratedFullPagePayment = 'INTEGRATED_FULL_PAGE_PAYMENT_20211118',
  appcuesOnboarding = 'APPCUES_ONBOARDING_EXPERIMENT_20210914',
  clusterBuilderPriceToggle = 'CLUSTER_BUILDER_PRICE_TOGGLE_EXPERIMENT_20210823',
  searchFistQueryExperiencePhase2 = 'FIRST_QUERY_EXPERIENCE_PHASE2_20211006',
  appCuesProductTour = 'ATLAS_APPCUES_PRODUCT_TOUR_20210917',
  searchDocsSideNav = 'ATLAS_SEARCH_DOCS_SIDE_NAV_20211117',
  m0ClusterCardDataCTA = 'M0_CLUSTER_CARD_DATA_CTA_20220105',
  m0ClusterCardConnectCTA = 'M0_CLUSTER_CARD_CONNECT_CTA_20220105',
  featureRibbonPaidTierGrouping = 'FEATURE_RIBBON_PAID_TIER_GROUPING_20220105',
  comparisonTableExperiment = 'COMPARISON_TABLE_EXPERIMENT_20220105',
  cloudFeatureUpdatesExperiment = 'CLOUD_FEATURE_UPDATES_EXPERIMENT_20220126',
  dataLimitWarningCtaExperiment = 'DATA_LIMIT_WARNING_CTA_EXPERIMENT_20220126',
  highlightConnect = 'HIGHLIGHT_CONNECT_20220105',
  sampleDataLoadOptionV1 = 'SAMPLE_DATA_LOAD_OPTION_V1_EXPERIMENT_20220216',
  commandLineToolsSidebar = 'COMMAND_LINE_TOOLS_SIDEBAR_20220306',
  commandLineToolsDataExplorer = 'COMMAND_LINE_TOOLS_DATA_EXPLORER_20220306',
  twoClickClusterUpgrade = 'TWO_CLICK_CLUSTER_UPGRADE_EXPERIMENT_20220302',
  atlasGoto = 'ATLAS_GOTO_20220420',
  localCurrencyExperiment = 'LOCAL_CURRENCY_EXPERIMENT_20220119',
  surfaceUserGoals = 'SURFACE_USER_GOALS_20220314',
  awsMarketplaceButton = 'AWS_MARKETPLACE_BUTTON_20220315',
  connectionMethodVSCode = 'CONNECTION_METHOD_VS_CODE_20220401',
  universalAddDataExperiment = 'UNIVERSAL_ADD_DATA_20220408',
  lowCarbonFootprintRegionsExperiment = 'LOW_CARBON_FOOTPRINT_REGIONS_20220502',
  clusterCardUpgradeToM2 = 'CLUSTER_CARD_UPGRADE_TO_M2_20220407',
}

/**
 * Test group names should be written in camelCase.
 * Include the suffix "Control" for the base condition,
 * and "Variant" with a brief descriptor for experimental groups:
 *
 * {experimentName}Control
 * {experimentName}VariantBasic
 * {experimentName}VariantFreeHobby
 *
 * The word 'Experiment' should not be used in test group names,
 * as that term is reserved for experiments at large. We are leaving
 * historical test group names intact until experiments are removed from the codebase.
 *
 * Please avoid using {experimentName} without a suffix,
 * as it complicates targeting in downstream analytics tools.
 */
export enum TestGroupName {
  fakeTestGroupForUnitTestingControl = 'fakeTestGroupNameForUnitTestingControl',
  fakeTestGroupForUnitTestingVariant = 'fakeTestGroupNameForUnitTestingVariant',
  pathSelectorTitlesVariantFreeAndHobby = 'pathSelectorTitlesVariantFreeAndHobby',
  pathSelectorTitlesVariantBasic = 'pathSelectorTitlesVariantBasic',
  fullPagePaymentClickTreatment = 'fullPagePaymentClickTreatment',
  emailVerificationExperiment = 'emailVerificationExperiment',
  flexConsultingUIExperiment = 'flexConsultingUIExperiment',
  clusterCardPreviewImageVariant = 'clusterCardPreviewImageVariant',
  integratedFullPagePaymentVariant = 'integratedFullPagePaymentVariant',
  newTeamMemberCredits = 'newTeamMemberCreditsVariation',
  appcuesOnboardingTreatment = 'appcuesOnboardingTreatment',
  clusterBuilderPriceToggleVariant = 'clusterBuilderPriceToggleVariant',
  atlasSearchDocsSideNavControl = 'atlasSearchDocsSideNavControl',
  atlasSearchDocsSideNavVariant = 'atlasSearchDocsSideNavVariant',
  m0ClusterCardDataCTALeftVariant = 'm0ClusterCardDataCTALeftVariant',
  m0ClusterCardDataCTARightVariant = 'm0ClusterCardDataCTARightVariant',
  m0ClusterCardConnectCTALeftVariant = 'm0ClusterCardConnectCTALeftVariant',
  m0ClusterCardConnectCTARightVariant = 'm0ClusterCardConnectCTARightVariant',
  featureRibbonPaidTierGroupingVariant = 'featureRibbonPaidTierGroupingVariant',
  comaprisonTableVariant = 'comparisonTableVariant',
  dataLimitWarningCtaControl = 'dataLimitWarningCtaControl',
  dataLimitWarningCtaVariant = 'dataLimitWarningCtaVariant',
  highlightConnectVariant = 'highlightConnectVariant',
  cmabToggleVariant = 'cmabToggleVariant',
  twoClickClusterUpgradeControl = 'twoClickClusterUpgradeControl',
  twoClickClusterUpgradeVariant = 'twoClickClusterUpgradeVariant',
  localCurrencyVariant = 'localCurrencyVariant',
  surfaceUserGoalsVariant = 'surfaceUserGoalsVariant',
  awsMarketplaceButtonVariant = 'awsMarketplaceButtonVariant',
  atlasGotoVariant = 'atlasGoToVariant',
  connectionMethodVSCodeVariant = 'connectionMethodVSCodeVariant',
  universalDataAddVariant = 'universalDataAddVariant',
  lowCarbonFootprintRegionsGreenPreselectedVariant = 'lowCarbonFootprintRegionsGreenPreselectedVariant',
  lowCarbonFootprintRegionsAnyPreselectedVariant = 'lowCarbonFootprintRegionsAnyPreselectedVariant',
  clusterCardUpgradeToM2Bulleted = 'clusterCardUpgradeToM2Bulleted',
  clusterCardUpgradeToM2Description = 'clusterCardUpgradeToM2Description',
}

export const PARTNER_CUSTOMER_LINK_NOTIFY_TEAM = CloudTeams.AtlasGrowth;

export const LOAD_SAMPLE_DATA_KEY = 'loadSampleData';

export enum AllocationPoint {
  ATLAS_REGISTRATION_SUCCEEDED = 'ATLAS_REGISTRATION_SUCCEEDED',
  CLUSTER_BUILDER_SEEN = 'CLUSTER_BUILDER_SEEN',
}
